import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Tooltip,
  Collapse,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { INDIAN_STATES } from "../data/indianStates";

// ── Inline SVG icons for left panel (no extra imports needed) ─────────────────
const IconBriefcase = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const IconBrain = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
  </svg>
);
const IconZap = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const IconUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconBot = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v4"/>
    <line x1="8" y1="16" x2="8" y2="16"/>
    <line x1="16" y1="16" x2="16" y2="16"/>
  </svg>
);
const IconGrid = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const IconShield = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconStar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

// ── City skyline SVG ──────────────────────────────────────────────────────────
function CitySkyline() {
  return (
      <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            opacity: 0.11,
            pointerEvents: 'none',
            lineHeight: 0,
          }}
      >
        <svg
            viewBox="0 0 500 220"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', display: 'block' }}
            preserveAspectRatio="xMidYMax meet"
        >
          {/* Back row – shorter buildings */}
          <rect x="0"   y="158" width="28" height="62"  rx="2" fill="white" />
          <rect x="32"  y="132" width="22" height="88"  rx="2" fill="white" />
          <rect x="58"  y="118" width="38" height="102" rx="2" fill="white" />
          <rect x="65"  y="100" width="10" height="120" rx="1" fill="white" />
          <rect x="100" y="148" width="28" height="72"  rx="2" fill="white" />
          <rect x="132" y="88"  width="35" height="132" rx="2" fill="white" />
          <rect x="141" y="66"  width="14" height="154" rx="1" fill="white" />
          <rect x="171" y="122" width="30" height="98"  rx="2" fill="white" />
          <rect x="205" y="60"  width="42" height="160" rx="2" fill="white" />
          <rect x="217" y="40"  width="16" height="180" rx="1" fill="white" />
          <rect x="252" y="108" width="30" height="112" rx="2" fill="white" />
          <rect x="286" y="86"  width="38" height="134" rx="2" fill="white" />
          <rect x="297" y="62"  width="12" height="158" rx="1" fill="white" />
          <rect x="328" y="130" width="28" height="90"  rx="2" fill="white" />
          <rect x="360" y="76"  width="36" height="144" rx="2" fill="white" />
          <rect x="370" y="52"  width="12" height="168" rx="1" fill="white" />
          <rect x="400" y="114" width="32" height="106" rx="2" fill="white" />
          <rect x="436" y="94"  width="30" height="126" rx="2" fill="white" />
          <rect x="470" y="132" width="30" height="88"  rx="2" fill="white" />

          {/* Window grid on key buildings */}
          {[58, 132, 205, 252, 286, 360, 400].map((bx, bi) => (
              [0, 1, 2].map(col =>
                  [0, 1, 2, 3].map(row => (
                      <rect
                          key={`w-${bi}-${col}-${row}`}
                          x={bx + 4 + col * 10}
                          y={135 + row * 15}
                          width={5} height={8}
                          rx="1"
                          fill="white"
                          opacity="0.55"
                      />
                  ))
              )
          ))}

          {/* Ground line */}
          <rect x="0" y="218" width="500" height="2" fill="white" />
        </svg>
      </Box>
  );
}

// ── Feature card component ────────────────────────────────────────────────────
const FeatureCard = ({ icon, title, desc, last }) => (
  <Box sx={{ display: "flex", gap: 2, mb: last ? 0 : 2.5 }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: "10px",
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "rgba(255,255,255,0.88)",
        backdropFilter: "blur(4px)",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{ color: "#fff", fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.35, mb: 0.4 }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.56)", fontSize: "0.8rem", lineHeight: 1.55 }}>
        {desc}
      </Typography>
    </Box>
  </Box>
);

// ── Trust badge ───────────────────────────────────────────────────────────────
const TrustBadge = ({ icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box sx={{ color: "rgba(147,197,253,0.8)", display: "flex", alignItems: "center" }}>{icon}</Box>
    <Typography sx={{ color: "rgba(255,255,255,0.48)", fontSize: "0.75rem", fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

// ── Decorative background SVG ─────────────────────────────────────────────────
const PanelDecor = () => (
  <Box
    sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}
  >
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 440 780"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
    >
      {/* Curved abstract lines */}
      <path d="M-60 220 Q 180 80, 400 320 T 520 580" stroke="rgba(96,165,250,0.07)" strokeWidth="1.2" fill="none"/>
      <path d="M-40 380 Q 160 230, 440 430" stroke="rgba(96,165,250,0.05)" strokeWidth="1" fill="none"/>
      <path d="M60 -40 Q 220 180, 180 400 T 340 680" stroke="rgba(96,165,250,0.06)" strokeWidth="1.5" fill="none"/>
      <path d="M380 -20 Q 200 200, 280 480" stroke="rgba(96,165,250,0.05)" strokeWidth="1" fill="none"/>
      {/* Sparse grid */}
      <line x1="0" y1="60" x2="440" y2="60" stroke="rgba(96,165,250,0.035)" strokeWidth="1"/>
      <line x1="0" y1="180" x2="440" y2="180" stroke="rgba(96,165,250,0.03)" strokeWidth="1"/>
      <line x1="0" y1="300" x2="440" y2="300" stroke="rgba(96,165,250,0.025)" strokeWidth="1"/>
      <line x1="110" y1="0" x2="110" y2="780" stroke="rgba(96,165,250,0.03)" strokeWidth="1"/>
      <line x1="280" y1="0" x2="280" y2="780" stroke="rgba(96,165,250,0.025)" strokeWidth="1"/>
      {/* Accent dots */}
      <circle cx="60" cy="110" r="2.5" fill="rgba(96,165,250,0.22)"/>
      <circle cx="140" cy="75" r="1.8" fill="rgba(96,165,250,0.16)"/>
      <circle cx="95" cy="165" r="1.8" fill="rgba(96,165,250,0.14)"/>
      <circle cx="320" cy="220" r="2.2" fill="rgba(96,165,250,0.12)"/>
      <circle cx="370" cy="160" r="1.6" fill="rgba(96,165,250,0.1)"/>
      <circle cx="400" cy="320" r="1.4" fill="rgba(96,165,250,0.1)"/>
      <circle cx="50" cy="300" r="1.6" fill="rgba(96,165,250,0.1)"/>
      {/* Top-right radial accent arc */}
      <circle cx="420" cy="60" r="120" stroke="rgba(59,130,246,0.06)" strokeWidth="1" fill="none"/>
      <circle cx="420" cy="60" r="200" stroke="rgba(59,130,246,0.04)" strokeWidth="1" fill="none"/>
    </svg>
  </Box>
);

// =============================================================================
// COLORS (unchanged from original)
// =============================================================================
const COLORS = {
  color: "#1E3A8A",
  hover: "#60A5FA",
  textPrimary: "#0A0F2C",
  textSecondary: "#475569",
  links: "#1E3A8A",
};

// =============================================================================
// REGISTER COMPONENT
// =============================================================================
function Register() {
  // ── All state (100% unchanged) ──────────────────────────────────────────────
  const [formData, setFormData] = useState({
    registrationType: "individual",
    email: "",
    password: "",
    reenterpassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    companyName: "",
    companyDomain: "",
    companyWebsite: "",
    businessRegistrationNumber: "",
    state: "",
    role: "candidate",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [companyVerificationDoc, setCompanyVerificationDoc] = useState(null);
  const [gstCertificate, setGstCertificate] = useState(null);
  const [panCard, setPanCard] = useState(null);
  const [professionalTax, setProfessionalTax] = useState(null);
  const [epfoRegistration, setEpfoRegistration] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success");
  const [duplicateEmailOpen, setDuplicateEmailOpen] = useState(false);
  const [duplicatePhoneOpen, setDuplicatePhoneOpen] = useState(false);

  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Google-onboarding mode (unchanged)
  const isGoogleOnboarding = new URLSearchParams(location.search).get("googleOnboarding") === "1";

  React.useEffect(() => {
    if (!isGoogleOnboarding) return;
    try {
      const raw = sessionStorage.getItem("google_onboarding_data");
      if (!raw) return;
      const gData = JSON.parse(raw);
      setFormData(prev => ({
        ...prev,
        registrationType: "company",
        email:     gData.email     || prev.email,
        firstName: gData.firstName || prev.firstName,
        lastName:  gData.lastName  || prev.lastName,
        role:      "admin",
      }));
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleOnboarding]);

  const isCompany = formData.registrationType === "company";

  // ── Validation (100% unchanged) ─────────────────────────────────────────────
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[+]?[0-9\s-]{10,15}$/;

  function getPasswordStrength(password) {
    if (!password) return { level: 0, label: "", barColor: "#e0e0e0", textColor: "#9e9e9e", percent: 0 };
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNum = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const len = password.length;
    if (len >= 12 && hasUpper && hasLower && hasNum && hasSpecial)
      return { level: 4, label: "Very Strong", barColor: "#1b5e20", textColor: "#1b5e20", percent: 100 };
    if (len >= 8 && hasUpper && hasLower && hasNum && hasSpecial)
      return { level: 3, label: "Strong", barColor: "#4caf50", textColor: "#2e7d32", percent: 75 };
    if (len >= 6 && ((hasUpper && hasLower) || (hasNum && hasLower) || (hasNum && hasUpper)))
      return { level: 2, label: "Medium", barColor: "#ff9800", textColor: "#e65100", percent: 50 };
    return { level: 1, label: "Weak", barColor: "#ef5350", textColor: "#c62828", percent: 25 };
  }

  function validateField(name, value) {
    let error = "";
    if (name === "firstName" && !value.trim()) error = "First name is required";
    if (name === "lastName" && !value.trim()) error = "Last name is required";
    if (name === "companyName" && isCompany && !value.trim()) error = "Company name is required";
    if (name === "companyWebsite" && isCompany && !String(value || "").trim()) error = "Company website is required";
    if (name === "companyDomain" && isCompany && !String(value || "").trim()) error = "Domain (Industry) is required";
    if (name === "state" && isCompany && !String(value || "").trim()) error = "State is required";
    if (name === "email" && !emailRegex.test(value)) error = "Enter a valid email address";
    if (name === "phone") {
      if (!value.trim()) error = isCompany ? "Contact phone is required" : "Phone is required";
      else if (value && !phoneRegex.test(value)) error = "Enter a valid phone number";
    }
    if (name === "address") {
      if (!value.trim()) error = isCompany ? "Company address is required" : "Address is required";
      else if (value && value.trim().length < 5) error = "Address must be at least 5 characters";
    }
    if (name === "password" && !value) error = "Password is required";
    return error;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    if (name === "password") setPasswordTouched(true);
  }

  function handleFileChange(e) {
    const file = e.target.files[0] || null;
    setCompanyVerificationDoc(file);
    if (file) setErrors(prev => ({ ...prev, companyVerificationDoc: "" }));
  }
  function handleGstFileChange(e) {
    const file = e.target.files[0] || null;
    setGstCertificate(file);
    if (file) setErrors(prev => ({ ...prev, gstCertificate: "" }));
  }
  function handlePanFileChange(e) {
    const file = e.target.files[0] || null;
    setPanCard(file);
    if (file) setErrors(prev => ({ ...prev, panCard: "" }));
  }
  function handleProfessionalTaxFileChange(e) {
    const file = e.target.files[0] || null;
    setProfessionalTax(file);
    if (file) setErrors(prev => ({ ...prev, professionalTax: "" }));
  }
  function handleEpfoFileChange(e) {
    const file = e.target.files[0] || null;
    setEpfoRegistration(file);
  }

  // ── handleSubmit (100% unchanged) ───────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    const newErrors = {
      firstName: validateField("firstName", formData.firstName),
      lastName: validateField("lastName", formData.lastName),
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
      phone: validateField("phone", formData.phone),
      address: validateField("address", formData.address),
      ...(isCompany
        ? {
            companyName: validateField("companyName", formData.companyName),
            companyDomain: validateField("companyDomain", formData.companyDomain),
            companyWebsite: validateField("companyWebsite", formData.companyWebsite),
            state: validateField("state", formData.state),
          }
        : {}),
    };
    if (isCompany && !companyVerificationDoc) newErrors.companyVerificationDoc = "Company verification document is required";
    if (companyVerificationDoc && companyVerificationDoc.size > 5 * 1024 * 1024) newErrors.companyVerificationDoc = "File size must not exceed 5MB";
    if (isCompany && !gstCertificate) newErrors.gstCertificate = "GST Registration Certificate is required";
    if (gstCertificate && gstCertificate.size > 5 * 1024 * 1024) newErrors.gstCertificate = "File size must not exceed 5MB";
    if (isCompany && !panCard) newErrors.panCard = "PAN Card of Company is required";
    if (panCard && panCard.size > 5 * 1024 * 1024) newErrors.panCard = "File size must not exceed 5MB";
    if (isCompany && !professionalTax) newErrors.professionalTax = "Professional Tax Registration document is required";
    if (professionalTax && professionalTax.size > 5 * 1024 * 1024) newErrors.professionalTax = "File size must not exceed 5MB";
    if (epfoRegistration && epfoRegistration.size > 5 * 1024 * 1024) newErrors.epfoRegistration = "File size must not exceed 5MB";
    if (formData.password !== formData.reenterpassword) newErrors.reenterpassword = "Passwords do not match";
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const pwChecks = [
        formData.password.length >= 8,
        /[A-Z]/.test(formData.password),
        /[a-z]/.test(formData.password),
        /[0-9]/.test(formData.password),
        /[^A-Za-z0-9]/.test(formData.password),
      ];
      if (!pwChecks.every(Boolean)) newErrors.password = "Password does not meet security requirements";
    }
    if (isGoogleOnboarding) {
      delete newErrors.password;
      delete newErrors.reenterpassword;
    }
    if (Object.values(newErrors).some(err => err)) {
      setErrors(newErrors);
      return;
    }
    try {
      setLoading(true);
      let payload;
      if (isCompany) {
        payload = new FormData();
        Object.keys(formData).forEach(key => {
          if (formData[key] !== undefined && formData[key] !== "") payload.append(key, formData[key]);
        });
        payload.append("companyVerificationDoc", companyVerificationDoc);
        payload.append("gstCertificate", gstCertificate);
        payload.append("panCard", panCard);
        payload.append("professionalTax", professionalTax);
        if (epfoRegistration) payload.append("epfoRegistration", epfoRegistration);
        if (isGoogleOnboarding) {
          try {
            const gData = JSON.parse(sessionStorage.getItem("google_onboarding_data") || "{}");
            if (gData.googleId)  payload.append("googleId",      gData.googleId);
            if (gData.picture)   payload.append("googlePicture", gData.picture);
            payload.append("password", "__google_onboarding__");
          } catch (_) {}
        }
      } else {
        payload = formData;
      }
      await register(payload);
      if (isGoogleOnboarding) sessionStorage.removeItem("google_onboarding_data");
      setModalType("success");
      setModalMessage(
        isGoogleOnboarding
          ? "Company profile submitted! Your account is under review. You will be notified once approved."
          : "Registration successful! Please check your email to verify your account."
      );
      setModalOpen(true);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      if (status === 409 && msg === "Phone number already registered") {
        setDuplicatePhoneOpen(true);
      } else if (status === 409) {
        setDuplicateEmailOpen(true);
      } else {
        setModalType("error");
        if (status === 413) {
          setModalMessage("File size is too large. Please ensure each uploaded document is under 5MB.");
        } else if (status === 400 || msg === "email is not exist") {
          setModalMessage("Email is not exist");
        } else if (!err.response) {
          setModalMessage("Registration failed. This may be due to a network error or large file uploads. Please check your internet connection and ensure individual file sizes are under 5MB.");
        } else {
          setModalMessage(msg || "Failed to register");
        }
        setModalOpen(true);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleModalClose = () => {
    setModalOpen(false);
    if (modalType === "success") navigate("/login");
  };

  // ── Left panel content (candidate) ──────────────────────────────────────────
  const candidateContent = (
    <>
      <Typography
        sx={{
          color: "#fff",
          fontWeight: 800,
          fontSize: { lg: "1.7rem", xl: "2rem" },
          lineHeight: 1.22,
          letterSpacing: "-0.02em",
          mb: 1.5,
        }}
      >
        Your Career<br />Starts Here
      </Typography>
      <Typography
        sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.875rem", lineHeight: 1.7, mb: 4, maxWidth: 340 }}
      >
        Create your professional profile, discover opportunities, and showcase your skills with AI-powered hiring.
      </Typography>
      <FeatureCard
        icon={<IconBriefcase />}
        title="Find Better Opportunities"
        desc="Discover jobs that match your skills and career goals."
      />
      <FeatureCard
        icon={<IconBrain />}
        title="AI-Powered Interviews"
        desc="Experience intelligent assessments designed to highlight your strengths."
      />
      <FeatureCard
        icon={<IconZap />}
        title="Fast & Simple Registration"
        desc="Complete your profile quickly and start applying with confidence."
        last
      />
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mt: "auto", pt: 4 }}>
        <TrustBadge icon={<IconShield />} label="Privacy Protected" />
        <TrustBadge icon={<IconCheck />} label="Secure Registration" />
        <TrustBadge icon={<IconStar />} label="AI-Powered Platform" />
      </Box>
    </>
  );

  // ── Left panel content (recruiter) ──────────────────────────────────────────
  const recruiterContent = (
    <>
      <Typography
        sx={{
          color: "#fff",
          fontWeight: 800,
          fontSize: { lg: "1.7rem", xl: "2rem" },
          lineHeight: 1.22,
          letterSpacing: "-0.02em",
          mb: 1.5,
        }}
      >
        Hire Smarter.<br />Build Exceptional Teams.
      </Typography>
      <Typography
        sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.875rem", lineHeight: 1.7, mb: 4, maxWidth: 340 }}
      >
        Accelerate hiring with AI-powered recruitment tools that help you source, assess, interview, and hire top talent efficiently.
      </Typography>
      <FeatureCard
        icon={<IconUsers />}
        title="Smart Candidate Matching"
        desc="Identify the right candidates faster with AI."
      />
      <FeatureCard
        icon={<IconBot />}
        title="Automated Interview Process"
        desc="Reduce manual effort through intelligent screening."
      />
      <FeatureCard
        icon={<IconGrid />}
        title="Organize & Collaborate"
        desc="Manage jobs, candidates, and hiring workflows in one place."
        last
      />
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mt: "auto", pt: 4 }}>
        <TrustBadge icon={<IconShield />} label="Enterprise Security" />
        <TrustBadge icon={<IconZap />} label="Faster Hiring" />
        <TrustBadge icon={<IconUsers />} label="Built for Growing Teams" />
      </Box>
    </>
  );

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <>
      {/* ── SPLIT-SCREEN WRAPPER ── */}
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", bgcolor: "#f1f5f9" }}>

        {/* ══════════════════════════════════════════════════════════════════════
            LEFT PROMOTIONAL PANEL
        ══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            width: { xs: "0", lg: "38%" },
            display: { xs: "none", lg: "flex" },
            flexShrink: 0,
            position: "relative",
            flexDirection: "column",
            overflow: "hidden",
            background:
            "linear-gradient(160deg, #0B1638 0%, #0D1C40 60%, #0F2050 100%)",
          }}
        >


          {/* ── Main content ── */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              px: 5,
              pt: 5,
              pb: "220px", // clear city skyline
              overflow: "hidden",
            }}
          >


            {/* Dynamic content */}
            {isCompany ? recruiterContent : candidateContent}
          </Box>

          {/* ── City skyline ── */}

          <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "220px",
                zIndex: 1,
                pointerEvents: "none",
                overflow: "hidden",

                /* Same background as the left panel */
                background:
                    "linear-gradient(160deg, #0B1638 0%, #0D1C40 60%, #0F2050 100%)",
              }}
          >
            <CitySkyline />
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════════════════
            RIGHT FORM PANEL
        ══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: 1,
            height: "100vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            py: { xs: 3, sm: 5 },
            px: { xs: 2, sm: 3 },
            bgcolor: "#f1f5f9",
          }}
        >
            {/* Floating Logo */}
            <Box
                sx={{
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    bgcolor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 20px rgba(11,22,56,0.13)",
                    mb: "-34px",
                    zIndex: 2,
                    border: "3px solid #FFFFFF",
                    outline: "1px solid #E5E7EB",
                    flexShrink: 0,
                }}
            >
                <img
                    src="/android-chrome-192x192.png"
                    alt="ExiHire"
                    style={{
                        width: 40,
                        height: 40,
                        objectFit: "contain",
                    }}
                />
            </Box>
          <Paper

            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "780px",

              p: { xs: 3, sm: 4, md: "56px 60px 48px" },
              borderRadius: "18px",
              border: "1px solid rgba(226,232,240,0.9)",
              boxShadow:
                "0 4px 24px rgba(15,23,42,0.07), 0 1px 3px rgba(15,23,42,0.05)",
              backgroundColor: "#fff",
              mb: 4,
            }}
          >
            {/* Mobile-only logo
            <Box
              sx={{
                display: { xs: "flex", lg: "none" },
                alignItems: "center",
                gap: 1.5,
                mb: 3,
              }}
            >
              <img
                src="/favicon.ico"
                alt="Exihire"
                style={{ width: 28, height: 28, objectFit: "contain" }}
              />
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>
                exihire
              </Typography>
            </Box>  */}

            {/* Form heading */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0A0F2C",
                  fontSize: { xs: "1.35rem", sm: "1.55rem" },
                  lineHeight: 1.3,
                  mb: 0.6,
                  textTransform: "capitalize",
                }}
              >
                {formData.registrationType === "company"
                  ? "start hiring smarter"
                  : "start your career with exihire"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.875rem" }}>
                {formData.registrationType === "company"
                  ? "Create your company account and start finding top talent."
                  : "Create your account and discover new opportunities."}
              </Typography>
            </Box>

            {/* Error alert */}
            {errors.form && errors.form !== "Google authentication is not configured." && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
                {errors.form}
              </Alert>
            )}

            {/* ── FORM (all fields 100% unchanged) ── */}
            <form onSubmit={handleSubmit} noValidate>
              {/* Registration type */}
              <Box
                sx={{
                  mb: 2.5,
                  p: "14px 16px",
                  bgcolor: "#f8fafc",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: 600, fontSize: "0.85rem", color: "#374151" }}
                >
                  Please select how you would like to proceed with your registration.
                </Typography>
                <FormControl>
                  <RadioGroup
                    row
                    name="registrationType"
                    value={formData.registrationType}
                    onChange={handleChange}
                    sx={{ gap: 6, flexWrap: "wrap" }}
                  >
                    <FormControlLabel
                      value="company"
                      control={<Radio size="small" />}
                      label="Company / Business"
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio size="small" />}
                      label="Individual"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* First / Last name */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 3,
                }}
              >
                <TextField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Box>

              {/* Company-specific row OR email */}
              {isCompany ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      label="Company Name"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      error={!!errors.companyName}
                      helperText={errors.companyName}
                    />
                    <TextField
                      select
                      label="Domain (Industry)"
                      name="companyDomain"
                      value={formData.companyDomain}
                      onChange={handleChange}
                      required
                      error={!!errors.companyDomain}
                      helperText={errors.companyDomain}
                    >
                      {[
                        "Software / IT",
                        "Human Resources (HR)",
                        "Finance / Banking",
                        "Healthcare",
                        "Education / EdTech",
                        "E-commerce",
                        "Manufacturing",
                        "Telecommunications",
                        "Consulting",
                        "Marketing / Advertising",
                        "Real Estate",
                        "Legal Services",
                        "Media / Entertainment",
                        "Logistics / Supply Chain",
                        "Retail",
                        "Automotive",
                        "Construction",
                        "Pharmaceutical",
                        "Energy / Utilities",
                        "Agriculture",
                        "Hospitality / Travel",
                        "Government / Public Sector",
                        "Non-Profit / NGO",
                        "Other",
                      ].map(domain => (
                        <MenuItem key={domain} value={domain}>
                          {domain}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <TextField
                    label="Company Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              )}

              {/* Password */}
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setPasswordTouched(true)}
                margin="normal"
                required
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onMouseDown={() => setShowPassword(true)}
                        onMouseUp={() => setShowPassword(false)}
                        onMouseLeave={() => setShowPassword(false)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password requirements (100% unchanged) */}
              {(() => {
                const pw = formData.password;
                const rules = [
                  { label: "Minimum 8 characters", met: pw.length >= 8 },
                  { label: "At least 1 uppercase letter (A-Z)", met: /[A-Z]/.test(pw) },
                  { label: "At least 1 lowercase letter (a-z)", met: /[a-z]/.test(pw) },
                  { label: "At least 1 number (0-9)", met: /[0-9]/.test(pw) },
                  { label: "At least 1 special character (!@#$%^&*)", met: /[^A-Za-z0-9]/.test(pw) },
                ];
                const allMet = rules.every(r => r.met);
                const strengthInfo = getPasswordStrength(pw);
                const isDefault = pw.length === 0;
                return (
                  <>
                    <Collapse in={passwordTouched || pw.length > 0}>
                      <Box
                        sx={{
                          mt: 1,
                          mb: 0.5,
                          p: 2,
                          borderRadius: "10px",
                          border: "1px solid",
                          borderColor: allMet ? "#a7f3d0" : "#e2e8f0",
                          background: allMet ? "rgba(167,243,208,0.07)" : "#fafafa",
                          transition: "border-color 0.3s ease, background 0.3s ease",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#374151",
                            display: "block",
                            mb: 1,
                            fontSize: "0.77rem",
                            letterSpacing: "0.03em",
                            textTransform: "uppercase",
                          }}
                        >
                          Password Requirements
                        </Typography>
                        {rules.map((rule, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              mb: i < rules.length - 1 ? 0.5 : 0,
                            }}
                          >
                            {isDefault ? (
                              <RadioButtonUnchecked sx={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }} />
                            ) : rule.met ? (
                              <CheckCircle sx={{ fontSize: 13, color: "#16a34a", flexShrink: 0 }} />
                            ) : (
                              <Cancel sx={{ fontSize: 13, color: "#dc2626", flexShrink: 0 }} />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.78rem",
                                color: isDefault ? "#6b7280" : rule.met ? "#16a34a" : "#dc2626",
                                fontWeight: isDefault ? 400 : 500,
                                transition: "color 0.2s ease",
                                lineHeight: 1.5,
                              }}
                            >
                              {rule.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                    {pw.length > 0 && (
                      <Box sx={{ mt: 1, mb: 0.5 }}>
                        <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                          {[1, 2, 3, 4].map(seg => (
                            <Box
                              key={seg}
                              sx={{
                                flex: 1,
                                height: 5,
                                borderRadius: 3,
                                backgroundColor:
                                  seg <= strengthInfo.level ? strengthInfo.barColor : "#e5e7eb",
                                transition: "background-color 0.35s ease",
                              }}
                            />
                          ))}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: strengthInfo.textColor,
                            fontWeight: 700,
                            fontSize: "0.74rem",
                            transition: "color 0.35s ease",
                          }}
                        >
                          {strengthInfo.label}
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}

              {/* Confirm password */}
              <TextField
                fullWidth
                label="Re-Enter Password"
                name="reenterpassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.reenterpassword}
                onChange={handleChange}
                margin="normal"
                required
                error={
                  !!errors.reenterpassword ||
                  (formData.reenterpassword.length > 0 &&
                    formData.password !== formData.reenterpassword)
                }
                helperText={errors.reenterpassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onMouseDown={() => setShowConfirmPassword(true)}
                        onMouseUp={() => setShowConfirmPassword(false)}
                        onMouseLeave={() => setShowConfirmPassword(false)}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Live confirm password indicator */}
              {formData.reenterpassword.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: -0.5, mb: 0.5 }}>
                  {formData.password === formData.reenterpassword ? (
                    <CheckCircle sx={{ fontSize: 14, color: "#16a34a" }} />
                  ) : (
                    <Cancel sx={{ fontSize: 14, color: "#dc2626" }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.78rem",
                      color: formData.password === formData.reenterpassword ? "#16a34a" : "#dc2626",
                      fontWeight: 600,
                      transition: "color 0.2s ease",
                    }}
                  >
                    {formData.password === formData.reenterpassword
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </Typography>
                </Box>
              )}

              {/* Company or individual extra fields */}
              {isCompany ? (
                <>
                  <TextField
                    fullWidth
                    label="Company Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    margin="normal"
                    required
                    error={!!errors.address}
                    helperText={errors.address}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <TextField
                      label="Company Website"
                      name="companyWebsite"
                      value={formData.companyWebsite}
                      onChange={handleChange}
                      required
                    />
                    <TextField
                      label="Contact Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      error={!!errors.phone}
                      helperText={errors.phone || "e.g. +1 234 567 8900"}
                    />
                  </Box>
                  <TextField
                    select
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    margin="normal"
                    required
                    error={!!errors.state}
                    helperText={errors.state || "Used to calculate GST on paid plans"}
                  >
                    {INDIAN_STATES.map(s => (
                      <MenuItem key={s.code} value={s.code}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Business Registration Number (optional)"
                    name="businessRegistrationNumber"
                    value={formData.businessRegistrationNumber}
                    onChange={handleChange}
                    margin="normal"
                  />

                  {/* File uploads — all unchanged */}
                  {[
                    {
                      label: "Company License / Registration / Identification Document *",
                      onChange: handleFileChange,
                      file: companyVerificationDoc,
                      errKey: "companyVerificationDoc",
                    },
                    {
                      label: "GST Registration Certificate *",
                      onChange: handleGstFileChange,
                      file: gstCertificate,
                      errKey: "gstCertificate",
                    },
                    {
                      label: "PAN Card of Company *",
                      onChange: handlePanFileChange,
                      file: panCard,
                      errKey: "panCard",
                    },
                    {
                      label: "Professional Tax Registration *",
                      onChange: handleProfessionalTaxFileChange,
                      file: professionalTax,
                      errKey: "professionalTax",
                    },
                  ].map(({ label, onChange, file, errKey }) => (
                    <Box key={errKey} sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                        {label}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          sx={{
                            whiteSpace: "nowrap",
                            borderColor: errors[errKey] ? "error.main" : undefined,
                          }}
                        >
                          Choose File
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={onChange}
                          />
                        </Button>
                        <Typography
                          variant="body2"
                          color={file ? "text.primary" : "text.secondary"}
                          sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {file ? file.name : "No file chosen"}
                        </Typography>
                      </Box>
                      {errors[errKey] ? (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                          {errors[errKey]}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                          Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                        </Typography>
                      )}
                    </Box>
                  ))}

                  {/* EPFO (optional) */}
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      EPFO Registration{" "}
                      <Typography component="span" variant="caption" color="text.secondary">
                        (Optional)
                      </Typography>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleEpfoFileChange}
                        />
                      </Button>
                      <Typography
                        variant="body2"
                        color={epfoRegistration ? "text.primary" : "text.secondary"}
                        sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {epfoRegistration ? epfoRegistration.name : "No file chosen"}
                      </Typography>
                    </Box>
                    {errors.epfoRegistration ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                        {errors.epfoRegistration}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        Accepted: PDF, PNG, JPG, JPEG (max 5MB)
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    margin="normal"
                    required
                    error={!!errors.phone}
                    helperText={errors.phone || "e.g. +1 234 567 8900"}
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    margin="normal"
                    required
                    error={!!errors.address}
                    helperText={errors.address}
                  />
                </>
              )}

              {/* Submit button (100% unchanged logic) */}
              {(() => {
                const pw = formData.password;
                const allMet = [
                  pw.length >= 8,
                  /[A-Z]/.test(pw),
                  /[a-z]/.test(pw),
                  /[0-9]/.test(pw),
                  /[^A-Za-z0-9]/.test(pw),
                ].every(Boolean);
                const pwMatch =
                  formData.reenterpassword.length > 0 && pw === formData.reenterpassword;
                const isPasswordValid = allMet && pwMatch;
                return (
                  <Tooltip
                    title={!isPasswordValid && !loading ? "Please complete all password requirements" : ""}
                    placement="top"
                    arrow
                  >
                    <span style={{ display: "block", marginTop: 24 }}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading || !isPasswordValid}
                        sx={{
                          background:
                            loading || !isPasswordValid
                              ? undefined
                              : "linear-gradient(90deg, #1E3A8A 0%, #1E40AF 100%)",
                          transition: "all 0.2s ease",
                          py: 1.5,
                          fontWeight: 700,
                          fontSize: "1rem",
                          borderRadius: "10px",
                        }}
                      >
                        {loading ? "Registering..." : "Register"}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
            </form>

            {/* Google Sign-Up (100% unchanged) */}
            {!isGoogleOnboarding && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                    {formData.registrationType === "company" ? "or continue with" : "or sign up with"}
                  </Typography>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                </Box>
                <GoogleSignInButton
                  onCredential={async credential => {
                    try {
                      setLoading(true);
                      const role = formData.registrationType === "company" ? "recruiter" : "candidate";
                      const result = await googleLogin(credential, role);
                      if (result && result.requiresEmailOtp) {
                        sessionStorage.setItem("otp_temp_token",   result.tempToken);
                        sessionStorage.setItem("otp_masked_email",  result.maskedEmail || "");
                        sessionStorage.setItem("otp_return_path", role === "recruiter" ? "/admin/dashboard" : "/candidate/dashboard");
                        navigate("/verify-email-otp");
                        return;
                      }
                      if (result && result.requiresRegistration) {
                        sessionStorage.setItem("google_onboarding_data", JSON.stringify(result.googleData));
                        navigate("/register?googleOnboarding=1");
                        return;
                      }
                      navigate(role === "recruiter" ? "/admin/dashboard" : "/candidate/dashboard");
                    } catch (err) {
                      setErrors(prev => ({
                        ...prev,
                        form: err.response?.data?.message || "Google Sign-Up failed. Please try again.",
                      }));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  text={formData.registrationType === "company" ? "continue_with" : "signup_with"}
                />
              </Box>
            )}

            {/* Already have account */}
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{ color: "#1E3A8A", fontWeight: 600, textDecoration: "none" }}
                >
                  Click here to login
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Box>
        {/* end right panel */}
      </Box>
      {/* end split-screen */}

      {/* ══ DIALOGS (100% unchanged) ══════════════════════════════════════════ */}

      {/* Duplicate Email */}
      <Dialog
        open={duplicateEmailOpen}
        onClose={() => setDuplicateEmailOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 0, pt: 3 }}>
          <ErrorOutline sx={{ fontSize: 56, color: "#d32f2f", display: "block", mx: "auto", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Registration Failed
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", px: 3, pt: 1 }}>
          <DialogContentText sx={{ color: "#4a4a4a", fontSize: "0.95rem", lineHeight: 1.7 }}>
            This email address is already associated with an existing account.
            <br /><br />
            Please login using your existing account or use a different email address.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            onClick={() => setDuplicateEmailOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#1E3A8A",
              color: "#1E3A8A",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { borderColor: "#006b6b", backgroundColor: "rgba(0,139,139,0.04)" },
            }}
          >
            Try Another Email
          </Button>
          <Button
            onClick={() => { setDuplicateEmailOpen(false); navigate("/login"); }}
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #1E3A8A 0%, #1E40AF 100%)",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { background: "linear-gradient(90deg, #006b6b 0%, #005a4b 100%)" },
            }}
          >
            Go To Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Phone */}
      <Dialog
        open={duplicatePhoneOpen}
        onClose={() => setDuplicatePhoneOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 0, pt: 3 }}>
          <ErrorOutline sx={{ fontSize: 56, color: "#d32f2f", display: "block", mx: "auto", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Registration Failed
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", px: 3, pt: 1 }}>
          <DialogContentText sx={{ color: "#4a4a4a", fontSize: "0.95rem", lineHeight: 1.7 }}>
            This phone number is already associated with an existing account.
            <br /><br />
            Please login using your existing account or use a different phone number.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            onClick={() => setDuplicatePhoneOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#1E3A8A",
              color: "#1E3A8A",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { borderColor: "#006b6b", backgroundColor: "rgba(0,139,139,0.04)" },
            }}
          >
            Try Another Phone Number
          </Button>
          <Button
            onClick={() => { setDuplicatePhoneOpen(false); navigate("/login"); }}
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #1E3A8A 0%, #1E40AF 100%)",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": { background: "linear-gradient(90deg, #006b6b 0%, #005a4b 100%)" },
            }}
          >
            Go To Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / Error modal */}
      <Dialog open={modalOpen} onClose={handleModalClose}>
        <DialogTitle>{modalType === "success" ? "Success" : "Error"}</DialogTitle>
        <DialogContent>
          <DialogContentText>{modalMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleModalClose}
            variant="contained"
            color={modalType === "success" ? "success" : "error"}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Register;
